// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Box, TextInput } from "@mantine/core";
import { Map, Marker } from 'react-map-gl';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { FC, useEffect, useRef, useState, forwardRef, useImperativeHandle, PointerEvent as ReactPointerEvent } from "react";
import mapboxgl from "mapbox-gl";
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import FreehandMode from './../FreeHandMode';
import useViewportControl from 'hooks/useViewportControl';

import styles from './../../map.module.scss'
import { MapPinsSourceAndLayers, MapRoutePinsSourceAndLayers, MapRouteSourceAndLayers, MapStartEndLocationSourceAndLayers, SearchResultPinsSourceAndLayers, SelectedPinOverlay } from "./MapSource";
import { ReactComponent as LassoIcon } from 'assets/lasso.svg';
import { ReactComponent as LocationIcon } from 'assets/location.svg';
import { ReactComponent as ListBulletIcon } from 'assets/ListBullets.svg';
import { ReactComponent as ArrowLeftIcon } from 'assets/arrow_left_alt.svg';
import { ReactComponent as MagnifyIcon } from "assets/magnifyingGlass.svg";
import * as turf from '@turf/turf';
import { DEFAULT_PIN_COLOR, Pin } from "Modules/Map/MapComponent";
import Toastr from "components/Toastr";
import LocationDebugPanel from "components/LocationDebugPanel";
import {
  describeGeoFailure,
  getPosition,
  startWatch,
  stopWatch,
  subscribe as subscribeToLocation,
  GeoFailure,
  GeoPosition,
} from "helpers/locationService";
import { accuracyCircleDiameterPx } from "helpers/mapGeometry";

const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
const CENTER_FOCAL_ZOOM = 15;

/**
 * Tag put on the eventData of every camera move WE initiate, so the `movestart`
 * listener can tell our own flyTo/easeTo apart from a real user pan. Exactly how
 * mapbox-gl's own GeolocateControl did it (it used `geolocateSource`).
 */
const FOLLOW_EVENT_DATA = { eisFollowSource: true };

export interface MapState {
  latLon: [number, number]
  focalZoom: number;
}

export interface MapCanvasHandle {
  getViewBoxPins: () => Pin[] | null;
  // returns: "lat,lon"
  getViewBoxCenter: () => string | null;
  getViewBoxRadius: () => number | null;
  centerMap: (latLon: [number, number]) => void;
};

interface Props {
  onPinSelected: (id: number | string | null) => void;
  onPinsLassoed: (id: (number | string)[] | null) => void;
  onViewPins: (ids: (string | number)[]) => void;
  onNavigateHome: () => void;
  onMapLoaded: () => void;
  mapState: MapState;
  pins: Pin[];
  searchResultPins: Pin[];
  routeData?: number[][];
  routePins?: {
    id: number | string;
    latLon: [number, number];
  }[];
  routeStartEndPins?: {
    id: number | string;
    latLon: [number, number];
  }[];
  selectedPinId?: number | string;
  isColorLabelActive: boolean;
  isColorLabelOnView: boolean;
  onColorLabeOnViewChange: (v: boolean) => void;
  onViewBoxChange: () => void;
  isModalOpen?: boolean;
  isDesktop?: boolean;
  onMapSearchChange?: (s: boolean) => void;
  isMapSearchActive?: boolean;
  selectedPractitioner: any;
}
const MapCanvas = forwardRef<MapCanvasHandle, Props>(({
  pins,
  searchResultPins,
  onPinSelected,
  onViewPins,
  onPinsLassoed,
  onMapLoaded,
  mapState,
  onNavigateHome,
  routeData,
  routePins,
  routeStartEndPins,
  isColorLabelOnView,
  isColorLabelActive,
  onColorLabeOnViewChange,
  selectedPinId,
  onViewBoxChange,
  isModalOpen = false,
  isDesktop = false,
  onMapSearchChange,
  isMapSearchActive,
  selectedPractitioner,
}, ref) => {
  const mapRef = useRef<mapboxgl.Map | undefined>();
  const [isMapReady, setIsMapReady] = useState(false);
  const [pinsGeoJSON, setPinsGeoJSON] = useState<GeoJSON.FeatureCollection<GeoJSON.Point,
    { id: string; selected: boolean }>>();
  const [selectedPinGeoJSON, setSelectedPinGeoJSON] = useState<GeoJSON.
    FeatureCollection<GeoJSON.Point, { id: string | number }>>();
  const [searchResultPinsGeoJSON, setSearchResultPinsGeoJSON] = useState<GeoJSON.FeatureCollection<GeoJSON.Point,
    { id: string; selected: boolean }>>();
  const [routeGeoJSON, setRouteGeoJSON] = useState<GeoJSON.Feature<GeoJSON.LineString>
    | null>(null);
  const [routePinsGeoJSON, setRoutePinsGeoJSON] = useState<GeoJSON.FeatureCollection<GeoJSON.Point,
    { id: number | string; }>>();
  const [routeStartEndPinsGeoJSON, setRouteStartEndPinsGeoJSON] = useState<GeoJSON.FeatureCollection<GeoJSON.Point,
    { id: string; type: "start" | "end" }>>();
  const [touchStart, setTouchStart] = useState<{ x: number; y: number; time: number } | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  // synchronous mirror of isLocating — a second tap must be evaluated against the
  // current value, not the value React has committed so far
  const isLocatingRef = useRef(false);
  const [userPosition, setUserPosition] = useState<GeoPosition | null>(null);
  // Follow mode, mirroring the old GeolocateControl's ACTIVE_LOCK: after a
  // successful Locate the camera keeps centring on the user, until the user
  // pans the map themselves (-> BACKGROUND: dot keeps updating, camera stops).
  // A ref, not state: the movestart listener is registered once and must read
  // the CURRENT value, and flipping it must not re-render the whole map.
  const isFollowingRef = useRef(false);
  // Is this component instance still mounted? Read by the async continuations of
  // the Locate request, whose cleanup has ALREADY run by the time a late fix
  // arrives — see the unmount effect and `centralizeMap` below.
  const isMountedRef = useRef(true);
  // latest fix, readable from the map listeners without re-registering them
  const userPositionRef = useRef<GeoPosition | null>(null);
  const accuracyCircleRef = useRef<HTMLDivElement | null>(null);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastCloseTime, setToastCloseTime] = useState(5000);
  // bumped on every failure so <Toastr> remounts and its close timer restarts —
  // otherwise a repeat of the SAME failure leaves showToast already true and the
  // user sees one stale toast instead of a fresh one per tap
  const [toastKey, setToastKey] = useState(0);
  const [isLocationDebugOpen, setIsLocationDebugOpen] = useState(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFiredRef = useRef(false);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const [isLassoMode, setIsLassoMode] = useState(false);
  const drawRef = useRef<any>(null);
  const pinsRef = useRef<Pin[]>();
  pinsRef.current = pins;

  // Control mobile viewport zoom settings - disable zoom in map view
  // BUT enable zoom when modal (baseball card) is open
  useViewportControl(true, isModalOpen);

  const getViewBoxPins = (): Pin[] | null => {
    if (!mapRef.current) return null;
    const bounds = mapRef.current.getBounds();
    if (!bounds) return null;

    const viewBoxPins = pins.filter(
      (p) => bounds.contains([p.latLon[1], p.latLon[0]])
    );
    return viewBoxPins;
  }

  const centerMap = (latLon: [number, number]) => {
    if (!mapRef.current) return null;
    mapRef.current.flyTo({
      center: [latLon[1], latLon[0]],
      zoom: CENTER_FOCAL_ZOOM
    });
  }

  const getViewBoxCenter = (): string | null => {
    if (!mapRef.current) return null;
    const bounds = mapRef.current.getBounds();
    if (!bounds) return null;
    return `${bounds.getCenter().lat}, ${bounds?.getCenter().lng}`;
  };

  const getViewBoxRadius = (): number | null => {
    if (!mapRef.current) return null;
    const bounds = mapRef.current.getBounds();
    if (!bounds) return null;
    const center = turf.point(
      [bounds.getCenter().lng, bounds.getCenter().lat]
    );
    const top = turf.point(
      [bounds.getCenter().lng, bounds.getNorth()]
    )
    return turf.distance(center, top, { units: "meters" });
  }

  /**
   * Size the accuracy circle from the fix's accuracy in metres.
   *
   * Imperative on purpose: this runs on every `zoom` frame, and pushing the
   * zoom through React state would re-render the whole map (and all its
   * sources) dozens of times per pinch. The maths lives in `helpers/mapGeometry`
   * so it is unit-testable — an inline exponent here is exactly how this circle
   * came to be drawn at half its true size.
   *
   * Deliberate difference from mapbox-gl: its `_updateCircleRadius` scales by the
   * MAP CENTRE's latitude (`tr._center.lat`); we use the FIX's latitude, which is
   * where the circle is actually drawn. Do not "fix" this to the centre.
   */
  const updateAccuracyCircle = () => {
    const el = accuracyCircleRef.current;
    const map = mapRef.current;
    const position = userPositionRef.current;
    if (!el || !map || !position) return;

    const diameterPx = accuracyCircleDiameterPx(
      position.accuracy,
      position.lat,
      map.getZoom()
    );
    if (!Number.isFinite(diameterPx) || diameterPx < 0) return;

    const diameter = `${diameterPx}px`;
    el.style.width = diameter;
    el.style.height = diameter;
  };

  /**
   * Locate button.
   *
   * Idempotent by design: while a request is in flight a re-tap is a NO-OP.
   * (The old implementation called mapbox-gl's GeolocateControl.trigger(), which
   * with trackUserLocation:true is a TOGGLE — so an impatient second tap
   * CANCELLED the very request the user was waiting for.)
   */
  const centralizeMap = () => {
    if (isLocatingRef.current) return;
    isLocatingRef.current = true;
    setIsLocating(true);

    getPosition({
      source: "locate",
      timeoutMs: 10000,
      maxAgeMs: 0,
      highAccuracy: true,
      // this runs inside the tap's own click handler: never let it inherit a
      // background request's promise, or iOS's transient user activation is lost
      // and the permission prompt can silently never appear
      gesture: true,
    })
      .then((position) => {
        // The user navigated away while the request was pending: the unmount
        // cleanup has already run `stopWatch()` — as a no-op, because no watch
        // existed yet — so a `startWatch` from here would register an OS watch
        // that NOTHING will ever stop, keeping the GPS warm for the rest of the
        // session. Bail before touching the watch, a dead map, or toast state.
        if (!isMountedRef.current) return;
        // re-entering follow mode: a second tap re-centres AND re-locks
        isFollowingRef.current = true;
        mapRef.current?.flyTo(
          {
            center: [position.lon, position.lat],
            zoom: CENTER_FOCAL_ZOOM,
          },
          // our own move — the movestart listener must not read it as a user pan
          FOLLOW_EVENT_DATA
        );
        // Continuous follow, started ONLY after the one-shot already succeeded.
        // Purely additive: if watchPosition never calls back (the suspected iOS
        // standalone failure) the user still has everything the tap delivered.
        // startWatch never throws and is idempotent, so a re-tap is harmless.
        startWatch({ source: "follow" });
      })
      .catch((error: GeoFailure) => {
        // same race, other branch: a failure arriving after unmount has nobody
        // left to toast at
        if (!isMountedRef.current) return;
        setToastMessage(describeGeoFailure(error));
        setToastCloseTime(error?.kind === "DENIED" ? 8000 : 5000);
        setShowToast(true);
        // 5 taps in airplane mode must produce 5 visibly fresh toasts, not one
        // toast then silence
        setToastKey((k) => k + 1);
      })
      .finally(() => {
        // the ref is unconditional — it is this instance's own re-tap guard
        isLocatingRef.current = false;
        if (!isMountedRef.current) return;
        setIsLocating(false);
      });
  };

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current !== null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleLocatePointerDown = (e: ReactPointerEvent) => {
    longPressFiredRef.current = false;
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    clearLongPressTimer();
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      longPressFiredRef.current = true;
      setIsLocationDebugOpen(true);
    }, 700);
  };

  const handleLocatePointerMove = (e: ReactPointerEvent) => {
    const start = pointerStartRef.current;
    if (!start || longPressTimerRef.current === null) return;
    if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > 10) {
      clearLongPressTimer();
    }
  };

  const handleLocatePointerUp = () => {
    clearLongPressTimer();
  };

  const handleLocateClick = () => {
    // a long-press opened the diagnostics panel; it must not also locate
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    centralizeMap();
  };

  const startDrawing = () => {
    if (!mapRef.current || !drawRef.current) {
      console.warn('Map or draw controls not initialized');
      return;
    }

    drawRef.current.deleteAll();

    if (!isLassoMode) {
      drawRef.current.changeMode('draw_polygon');
    } else {
      drawRef.current.changeMode('simple_select');
    }

    setIsLassoMode(!isLassoMode);
  };

  const updateArea = (e: any) => {
    const data = drawRef.current.getAll();

    if (data.features.length > 0) {
      const polygon = data.features[0];
      const lassoedPinsIds: (number | string)[] = [];

      pinsRef.current?.forEach((p) => {
        const [lat, lon] = p.latLon;

        const point = turf.point([Number(lon), Number(lat)]);
        const isInside = turf.booleanPointInPolygon(point, polygon as any);

        if (isInside) {
          lassoedPinsIds.push(p.id);
        }

      });
      onPinsLassoed(lassoedPinsIds);
      setIsLassoMode(false);
    } else {
      if (e.type === 'draw.delete') {
        onPinsLassoed(null)
      }
    }
  }


  const mountMapboxDraw = () => {
    if (!mapRef.current) return;

    drawRef.current = new MapboxDraw({
      controls: {
        polygon: false,
        trash: false,
        point: false,
        combine_features: false,
        uncombine_features: false,
        line_string: false,
      },
      modes: {
        ...MapboxDraw.modes,
        draw_polygon: FreehandMode,
      },
    });
    mapRef.current.addControl(drawRef.current);
    const map = mapRef.current;
    map.on('draw.create', updateArea);
    map.on('draw.delete', updateArea);
    map.on('draw.update', updateArea);
    return () => {
      if (mapRef.current && drawRef.current) {
        const map = mapRef.current;
        map.off('draw.create', updateArea);
        map.off('draw.delete', updateArea);
        map.off('draw.update', updateArea);
        map.removeControl(drawRef.current);
      }
    };
  };

  useImperativeHandle(ref, () => ({
    getViewBoxPins,
    getViewBoxCenter,
    getViewBoxRadius,
    centerMap,
  }));

  useEffect(() => {
    // COMPLETE ZOOM PREVENTION: Disable ALL browser zoom while map view is active
    // BUT allow zoom inside modals/drawers for accessibility
    console.log('Map mounted: Disabling all browser zoom');

    document.body.style.overscrollBehavior = 'none';
    if (mapRef.current) {
      mapRef.current.getCanvas().style.touchAction = 'none';
    }

    // Helper to check if event target is inside a modal or drawer
    const isInsideModal = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof Element)) return false;
      // Check for Mantine modal/drawer elements
      return !!(
        target.closest('.mantine-Modal-root') ||
        target.closest('.mantine-Modal-content') ||
        target.closest('.mantine-Drawer-root') ||
        target.closest('.mantine-Drawer-content') ||
        target.closest('[data-mantine-modal]') ||
        target.closest('[role="dialog"]')
      );
    };

    // 1. Prevent keyboard zoom (Cmd/Ctrl + Plus/Minus/0)
    const preventKeyboardZoom = (e: KeyboardEvent) => {
      if (isInsideModal(e.target)) return;
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '0' || e.key === '=')) {
        e.preventDefault();
      }
    };

    // 2. Prevent ALL trackpad/mouse wheel zoom globally
    const preventWheelZoom = (e: WheelEvent) => {
      if (isInsideModal(e.target)) return;
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };

    // 3. Prevent gesture zoom (Safari/WebKit)
    const preventGestureZoom = (e: Event) => {
      if (isInsideModal(e.target)) return;
      e.preventDefault();
    };

    // Add global listeners
    document.addEventListener('keydown', preventKeyboardZoom);
    document.addEventListener('wheel', preventWheelZoom, { passive: false });
    document.addEventListener('gesturestart', preventGestureZoom);
    document.addEventListener('gesturechange', preventGestureZoom);
    document.addEventListener('gestureend', preventGestureZoom);

    // Cleanup on unmount - restore normal zoom behavior
    return () => {
      console.log('Map unmounted: Re-enabling browser zoom');
      document.removeEventListener('keydown', preventKeyboardZoom);
      document.removeEventListener('wheel', preventWheelZoom);
      document.removeEventListener('gesturestart', preventGestureZoom);
      document.removeEventListener('gesturechange', preventGestureZoom);
      document.removeEventListener('gestureend', preventGestureZoom);
      document.body.style.overscrollBehavior = 'auto';
    };
  }, []);

  // Additional touch handling for map interactions (simplified since zoom is globally disabled)
  useEffect(() => {
    // Prevent iOS zoom on double-tap
    let lastTouchEnd = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let touchStartTime = 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let initialTouchDistance = 0;

    const preventZoom = (e: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
        e.stopPropagation();
      }
      lastTouchEnd = now;
    };

    // Enhanced pinch zoom prevention that tracks gesture intent
    const handleTouchStart = (e: TouchEvent) => {
      touchStartTime = Date.now();

      // If multi-touch (pinch gesture starting)
      if (e.touches.length === 2) {
        // Check if ANY touch is near the map (even if one is outside)
        const mapRect = mapRef.current?.getContainer()?.getBoundingClientRect();
        if (mapRect) {
          const expandedBounds = 100; // More generous for touch

          // Check if either touch point is near the map
          const touch1Near = (
            e.touches[0].clientX >= (mapRect.left - expandedBounds) &&
            e.touches[0].clientX <= (mapRect.right + expandedBounds) &&
            e.touches[0].clientY >= (mapRect.top - expandedBounds) &&
            e.touches[0].clientY <= (mapRect.bottom + expandedBounds)
          );

          const touch2Near = (
            e.touches[1].clientX >= (mapRect.left - expandedBounds) &&
            e.touches[1].clientX <= (mapRect.right + expandedBounds) &&
            e.touches[1].clientY >= (mapRect.top - expandedBounds) &&
            e.touches[1].clientY <= (mapRect.bottom + expandedBounds)
          );

          // If either touch is near map, prevent browser zoom
          if (touch1Near || touch2Near) {
            e.preventDefault();
          }
        } else {
          // Fallback: always prevent on multi-touch
          e.preventDefault();
        }

        // Calculate initial distance between touches
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        initialTouchDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
      }
      // Single touch - might be start of tap or pan
      else if (e.touches.length === 1) {
        // Don't prevent default yet - let map handle panning
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // For pinch gestures, always prevent viewport zoom
      if (e.touches.length === 2) {
        e.preventDefault();

        // Let Mapbox handle the actual map zoom
        // but prevent iOS from zooming the viewport
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );

        // Optional: Add custom zoom handling if needed
        // const zoomDelta = currentDistance - initialTouchDistance;
      }
    };

    // Chrome desktop: Prevent trackpad pinch zoom
    const handleWheel = (e: WheelEvent) => {
      // Chrome fires wheel events with ctrlKey=true for pinch gestures on trackpad
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        // The map will still zoom via Mapbox's own handlers
        // but we prevent the browser viewport zoom
      }
    };

    const mapContainer = mapRef.current?.getContainer();
    if (mapContainer) {
      // Add touch event listeners with passive: false to allow preventDefault
      mapContainer.addEventListener('touchend', preventZoom, { passive: false });
      mapContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
      mapContainer.addEventListener('touchmove', handleTouchMove, { passive: false });

      // Chrome desktop: Prevent trackpad pinch from zooming browser
      mapContainer.addEventListener('wheel', handleWheel, { passive: false });

      // Note: Document-level gesture prevention is now handled in the first useEffect
      // with modal detection. No need to duplicate here.

      return () => {
        mapContainer.removeEventListener('touchend', preventZoom);
        mapContainer.removeEventListener('touchstart', handleTouchStart);
        mapContainer.removeEventListener('touchmove', handleTouchMove);
        mapContainer.removeEventListener('wheel', handleWheel);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current]);

  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current;
      map.on('draw.modechange', (e) => {
        const mapCanvas = map.getCanvas();
        // If entering a draw mode
        if ((e as any).mode.startsWith('draw')) {
          document.body.style.overscrollBehavior = 'none';
          mapCanvas.style.touchAction = 'none';
        } else {
          // Reset when exiting draw mode
          document.body.style.overscrollBehavior = 'auto';
          mapCanvas.style.touchAction = 'auto';
          drawRef.current.deleteAll();
        }
      });

      // exposing  this to allow tests with playwright
      if (process.env.NODE_ENV !== "production") {
        window.e2eMapInstance = mapRef.current;
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current]);

  useEffect(() => {
    if (routeData) {
      const g: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: routeData,
        }
      };
      setRouteGeoJSON(g);
    } else {
      setRouteGeoJSON(null);
    }
  }, [routeData]);

  useEffect(() => {
    if (mapRef.current && isMapReady && !drawRef.current) {
      mountMapboxDraw();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current, isMapReady]);

  // Blue "you are here" dot. Rendered by us now instead of by GeolocateControl,
  // fed by whichever part of the app last obtained a fix — including the watch,
  // which publishes through the same channel.
  useEffect(
    () =>
      subscribeToLocation((position) => {
        userPositionRef.current = position;
        setUserPosition(position);
        // The dot updates on every fix; the CAMERA only while we hold the lock,
        // so a user who has panned away is never dragged back (the old
        // control's ACTIVE_LOCK -> BACKGROUND transition).
        if (!position || !isFollowingRef.current) return;
        mapRef.current?.easeTo(
          // no zoom: follow must not fight a zoom the user just made
          { center: [position.lon, position.lat] },
          FOLLOW_EVENT_DATA
        );
      }),
    []
  );

  // keep the accuracy circle in step with each new fix (zoom is handled by the
  // map listener below, which fires without a React render)
  useEffect(() => {
    updateAccuracyCircle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPosition, isMapReady]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    const onMoveStart = (e: any) => {
      // A move WE started (the Locate flyTo, or a follow easeTo) carries our tag
      // and must never be mistaken for the user panning — otherwise follow mode
      // would cancel itself on the very move that entered it.
      if (e?.eisFollowSource) return;
      // a window/orientation resize is not a gesture either (mapbox's own check)
      if (e?.originalEvent?.type === "resize") return;
      // the user took over: stop moving the camera, keep updating the dot
      isFollowingRef.current = false;
    };

    map.on("movestart", onMoveStart);
    map.on("zoom", updateAccuracyCircle);
    return () => {
      map.off("movestart", onMoveStart);
      map.off("zoom", updateAccuracyCircle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapReady]);

  // never leave a long-press timer, a follow lock or an OS watch running past
  // unmount — a leaked watchPosition keeps the GPS warm for the whole session
  useEffect(() => {
    // set on MOUNT, not just at declaration: StrictMode mounts, cleans up and
    // remounts the same instance, and a flag only ever cleared would leave
    // Locate permanently inert after that first synthetic cleanup
    isMountedRef.current = true;
    return () => {
      // read by the pending Locate continuations, which can still run after this
      isMountedRef.current = false;
      clearLongPressTimer();
      isFollowingRef.current = false;
      stopWatch();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const features: GeoJSON.Feature<GeoJSON.Point, { id: string; selected: boolean, color: string }>[] = pins.map((p) => {
      const [lat, lon] = p.latLon;
      return {
        type: 'Feature',
        properties: {
          id: p.id as any,
          selected: false,
          color: p?.color ? p.color : DEFAULT_PIN_COLOR,
        },
        geometry: {
          type: 'Point',
          coordinates: [lon, lat],
        },
      };
    });
    setPinsGeoJSON({
      type: "FeatureCollection",
      features
    });
  }, [pins]);

  useEffect(() => {
    const defaultAddressId = selectedPractitioner?.default_address;
    const address = selectedPractitioner?.addresses?.find(
      (x: any) => x.id === defaultAddressId
    );
    if (address) {
      const features: GeoJSON.Feature<GeoJSON.Point, { id: number | string; }> = {
        type: "Feature",
        properties: {
          id: selectedPractitioner?.id || "",
        },
        geometry: {
          type: 'Point',
          coordinates: [
            address.longitude,
            address.latitude
          ],
        },
      };
      setSelectedPinGeoJSON({
        type: "FeatureCollection",
        features: [features],
      });
    }
  }, [selectedPractitioner])

  useEffect(() => {
    const features: GeoJSON.Feature<GeoJSON.Point, { id: string; selected: boolean, color: string }>[] = searchResultPins.map((p) => {
      const [lat, lon] = p.latLon;
      return {
        type: 'Feature',
        properties: {
          id: p.id as any,
          selected: false,
          color: p?.color ? p.color : DEFAULT_PIN_COLOR,
        },
        geometry: {
          type: 'Point',
          coordinates: [lon, lat],
        },
      };
    });
    setSearchResultPinsGeoJSON({
      type: "FeatureCollection",
      features
    });
  }, [searchResultPins]);

  useEffect(() => {
    if (routePins) {
      const features: GeoJSON.Feature<GeoJSON.Point, { id: number | string }>[] = [];
      routePins
        .forEach((p) => {
          const lon = p.latLon[1];
          const lat = p.latLon[0];
          features.push({
            type: "Feature",
            properties: {
              id: p.id,
            },
            geometry: {
              type: 'Point',
              coordinates: [lon, lat]
            }
          })
        });

      const ds: GeoJSON.FeatureCollection<GeoJSON.Point, { id: number | string }> = {
        type: "FeatureCollection",
        features
      }
      setRoutePinsGeoJSON(ds);
    } else {
      setRoutePinsGeoJSON(undefined);
    }
  }, [routePins]);

  useEffect(() => {
    console.log("start end", routeStartEndPins)
    const features: GeoJSON.Feature<GeoJSON.Point, { id: string; type: "start" | "end" }>[] = [];
    
    if (routeStartEndPins && routeStartEndPins.length > 0) {
      const lastIndex = routeStartEndPins.length -1
      if (routeStartEndPins[0]?.id === "start") {
        const lat = routeStartEndPins[0].latLon[0];
        const lon = routeStartEndPins[0].latLon[1];
        features.push({
          type: 'Feature',
          properties: {
            id: `start`,
            type: 'start'
          },
          geometry: {
            type: 'Point',
            coordinates: [lon, lat]
          }
        });
      }
      if (routeStartEndPins[lastIndex]?.id === "end") {
        const lat = routeStartEndPins[lastIndex].latLon[0];
        const lon = routeStartEndPins[lastIndex].latLon[1];
        features.push({
          type: 'Feature',
          properties: {
            id: `end`,
            type: 'end'
          },
          geometry: {
            type: 'Point',
            coordinates: [lon, lat]
          }
        });
      }
    }

    const ds: GeoJSON.FeatureCollection<GeoJSON.Point, { id: string; type: "start" | "end" }> = {
      type: "FeatureCollection",
      features
    }
    setRouteStartEndPinsGeoJSON(ds);
  }, [routeStartEndPins]);

  useEffect(() => {
    if (!mapRef.current || !isMapReady) return;
    
    if (!routeStartEndPinsGeoJSON) {
      const source = mapRef.current.getSource('start-end-locations-source') as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
    }
  }, [routeStartEndPinsGeoJSON, isMapReady]);

  useEffect(() => {
    if (!mapRef.current || !isMapReady) return;
    
    if (!routePinsGeoJSON || routePinsGeoJSON.features.length === 0) {
      const source = mapRef.current.getSource('route-source') as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({ type: 'FeatureCollection', features: [] });
      }
    }
  }, [routePinsGeoJSON, isMapReady]);

  return (
    <Box style={{ zIndex: 1000 }}>
      <Map
        ref={mapRef as any}
        style={{
          position: 'absolute',
          width: '100vw',
          height: '100%',
          top: '0',
          left: '0'
        }}
        touchPitch={false}
        dragRotate={false}
        touchZoomRotate={{
          disableRotation: true
        }}
        onTouchStart={(e) => {
          setTouchStart({
            x: e.point.x,
            y: e.point.y,
            time: Date.now()
          });
        }}
        onTouchEnd={(e) => {
          if (!touchStart) return;

          const touchEnd = {
            x: e.point.x,
            y: e.point.y,
            time: Date.now()
          };

          const distance = Math.sqrt(
            Math.pow(touchEnd.x - touchStart.x, 2) +
            Math.pow(touchEnd.y - touchStart.y, 2)
          );
          const duration = touchEnd.time - touchStart.time;

          if (duration < 300 && distance < 10) {
            // Guard against race condition when map tiles aren't fully loaded
            let featureEvent: mapboxgl.MapboxGeoJSONFeature | undefined;
            try {
              [featureEvent] = e.target.queryRenderedFeatures(e.point);
            } catch (err) {
              console.warn('Map query failed - tiles may not be loaded:', err);
              return;
            }

            if (featureEvent && (featureEvent.layer?.id === "clusters" || featureEvent.layer?.id === "cluster-count")) {
              e.preventDefault();
              const clusterId = featureEvent.properties?.cluster_id;
              const coordinates = (featureEvent.geometry as GeoJSON.Point).coordinates;
              const source = mapRef.current?.getSource('practitioners-source') as mapboxgl.GeoJSONSource;

              if (source && clusterId !== undefined && mapRef.current) {
                source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                  if (err || !mapRef.current) return;

                  const targetZoom = Math.max(12, (zoom || 10) + 1);

                  mapRef.current.flyTo({
                    center: [coordinates[0], coordinates[1]],
                    zoom: targetZoom,
                    speed: 2,
                    curve: 1.5,
                    bearing: 0,
                    pitch: 0,
                    essential: true,
                  });
                });
              }
              return;
            }

            onPinSelected(null);

            if (featureEvent && (featureEvent.layer?.id === "unclustered-point" || featureEvent.layer?.id === "route-points"
              || featureEvent.layer?.id === "search-result-point"
              || featureEvent.layer?.id === "start-location-points"
              || featureEvent.layer?.id === "end-location-points"
            )) {
              onPinSelected(featureEvent?.properties?.id)
            } else {
              onPinSelected(null);
            }
          }
          onViewBoxChange();
          setTouchStart(null);
        }}
        onClick={(e) => {
          // Guard against race condition when map tiles aren't fully loaded
          let featureEvent: mapboxgl.MapboxGeoJSONFeature | undefined;
          try {
            [featureEvent] = e.target.queryRenderedFeatures(e.point);
          } catch (err) {
            console.warn('Map query failed - tiles may not be loaded:', err);
            return;
          }
          if (featureEvent && (featureEvent.layer?.id === "clusters" || featureEvent.layer?.id === "cluster-count")) {
            e.preventDefault();
            const clusterId = featureEvent.properties?.cluster_id;
            const coordinates = (featureEvent.geometry as GeoJSON.Point).coordinates;
            const source = mapRef.current?.getSource('practitioners-source') as mapboxgl.GeoJSONSource;

            if (source && clusterId !== undefined && mapRef.current) {
              source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err || !mapRef.current) return;

                const targetZoom = Math.max(12, (zoom || 10) + 1);

                mapRef.current.flyTo({
                  center: [coordinates[0], coordinates[1]],
                  zoom: targetZoom,
                  speed: 2,
                  curve: 1.5,
                  bearing: 0,
                  pitch: 0,
                  essential: true,
                });
              });
            }
            return;
          }

          // Only reset state if not clicking on cluster
          onPinSelected(null);
          if (featureEvent && ((featureEvent.layer?.id === "unclustered-point") || (featureEvent.layer?.id === "route-points")
            || (featureEvent.layer?.id === "search-result-point")
            || (featureEvent.layer?.id === "start-location-points")
            || (featureEvent.layer?.id === "end-location-points"))) {
            onPinSelected(featureEvent?.properties?.id);
          } else {
            onPinSelected(null);
          }
        }}
        onLoad={() => {
          mapRef?.current?.loadImage("/icons/red-marker.png", (err, img) => {
            if (err || !img) throw err;
            mapRef?.current?.addImage('custom-marker', img, { sdf: true });
          });
          mapRef?.current?.loadImage("/icons/route-marker.png", (err, img) => {
            if (err || !img) throw err;
            mapRef?.current?.addImage('custom-marker-route', img);
          });
          mapRef?.current?.loadImage("/icons/selected-marker-green.png", (err, img) => {
            if (err || !img) throw err;
            mapRef?.current?.addImage('selected-marker-green', img);
          });
          mapRef?.current?.loadImage("/icons/search-marker.png", (err, img) => {
            if (err || !img) throw err;
            mapRef?.current?.addImage('custom-search-marker', img);
          });

          onMapLoaded();
          setIsMapReady(true);
        }}
        initialViewState={{
          longitude: mapState.latLon[1],
          latitude: mapState.latLon[0],
          zoom: mapState.focalZoom,
          bearing: 0,
          pitch: 0,
        }}
        onData={(e: any) => {
          if (e?.sourceId === "practitioners-source") {
            const map = mapRef.current;
            const features = map?.querySourceFeatures("practitioners-source", {
              sourceLayer: "unclustered-points",
            });
            const idsToFetch: number[] = [];
            features?.forEach((f) => {
              if (!f?.properties?.point_count) {

                if (f?.properties?.id && !idsToFetch.includes(f?.properties?.id)) {
                  idsToFetch.push(f.properties.id);
                }
              }
            });
            if (idsToFetch.length > 0) {
              onViewPins(idsToFetch);
            }
          }
        }}
        mapStyle="mapbox://styles/mapbox/standard"
        mapboxAccessToken={mapboxToken}
      >
        {isMapReady && (
          <MapPinsSourceAndLayers
            id="practitioners-source"
            selectedPinId={selectedPinId}
            type="geojson"
            data={pinsGeoJSON}
            cluster={true}
            clusterMaxZoom={8}
            clusterRadius={50} />
        )}
        {isMapReady && routeGeoJSON && (
          <MapRouteSourceAndLayers
            id="route"
            type="geojson"
            data={routeGeoJSON} />
        )}
        {isMapReady && routePinsGeoJSON && routePinsGeoJSON.features.length > 0 && (
          <>
            <MapRoutePinsSourceAndLayers
              id="route-source"
              type="geojson"
              cluster={false}
              data={routePinsGeoJSON} />
            <SelectedPinOverlay
              id="selected-overlay-route"
              type="geojson"
              data={routePinsGeoJSON as any}
              selectedPinId={selectedPinId} />
          </>
        )}

        {isMapReady && routeStartEndPinsGeoJSON && routeStartEndPinsGeoJSON.features.length > 0 && (
          <>
            <MapStartEndLocationSourceAndLayers
              id="start-end-locations-source"
              type="geojson"
              
              data={routeStartEndPinsGeoJSON} />
            <SelectedPinOverlay
              id="selected-overlay-start-end"
              type="geojson"
              data={routeStartEndPinsGeoJSON}
              selectedPinId={selectedPinId} />
          </>
        )}

        {isMapReady && searchResultPinsGeoJSON && (
          <>
            <SearchResultPinsSourceAndLayers
              id="search-result"
              type="geojson"
              data={searchResultPinsGeoJSON} />
            <SelectedPinOverlay
              id="selected-overlay-search-result"
              type="geojson"
              data={searchResultPinsGeoJSON}
              selectedPinId={selectedPinId} />
          </>
        )}

        {/* Selected pin overlay - renders on top of all other layers */}
        {isMapReady && pinsGeoJSON && selectedPinGeoJSON && (
          <SelectedPinOverlay
            id="selected-overlay"
            type="geojson"
            data={selectedPinGeoJSON}
            selectedPinId={selectedPinId} />
        )}

        {/* Accuracy circle, under the dot. Reuses mapbox-gl's own class so the
            fill/radius styling comes from the already-imported mapbox-gl.css;
            only the diameter is ours, set imperatively in updateAccuracyCircle. */}
        {isMapReady && userPosition && (
          <Marker
            longitude={userPosition.lon}
            latitude={userPosition.lat}>
            <div
              ref={accuracyCircleRef}
              className="mapboxgl-user-location-accuracy-circle" />
          </Marker>
        )}

        {/* "You are here" dot. Reuses mapbox-gl's own class names so the styling
            comes from the already-imported mapbox-gl.css and needs no new CSS.

            The heading arrow (mapboxgl-user-location-heading) is deliberately
            NOT restored: it needs DeviceOrientationEvent.requestPermission(),
            i.e. a SECOND iOS permission dialog stacked on the location one, on
            the first tap of a button whose whole problem was that its prompt
            never appeared. Dropped on purpose — not an oversight. */}
        {isMapReady && userPosition && (
          <Marker
            longitude={userPosition.lon}
            latitude={userPosition.lat}>
            <div className="mapboxgl-user-location">
              <div className="mapboxgl-user-location-dot" />
            </div>
          </Marker>
        )}
      </Map>
      <div
        className={styles.toolBox}
      >
        {!isDesktop && (
            <ArrowLeftIcon
              style={{ color: "white", cursor: 'pointer' }}
              onClick={() => onNavigateHome()}
              width='35px' height='35px'
            />
        )}
        <LocationIcon
          style={{
            color: "white",
            cursor: 'pointer',
            // visible busy state: a fix can take several seconds
            opacity: isLocating ? 0.4 : 1,
            transition: 'opacity 150ms ease',
            // stop iOS turning the diagnostics long-press into a callout/selection
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}
          onClick={handleLocateClick}
          onPointerDown={handleLocatePointerDown}
          onPointerMove={handleLocatePointerMove}
          onPointerUp={handleLocatePointerUp}
          onPointerCancel={handleLocatePointerUp}
          onPointerLeave={handleLocatePointerUp}
          width='35px' height='35px'
        />
        <LassoIcon
          style={{
            stroke: isLassoMode ? 'lightgreen' : 'white',
            cursor: 'pointer'
          }}
          onClick={startDrawing}
          width='35px'
          height='35px'
        />
        {!isDesktop && (
          <MagnifyIcon
            style={{
              color: isMapSearchActive ? "lightgreen" : "white",
              cursor: 'pointer'
            }}
            onClick={() => {
              if (onMapSearchChange) {
                onMapSearchChange(!isMapSearchActive);
              }
            }}
            width='30px'
            height='30px'
          />
        )}
      </div>
      <Toastr
        key={toastKey}
        closeTime={toastCloseTime}
        backgroundColor={'#E64B37'}
        message={toastMessage}
        isVisible={showToast}
        onClose={() => setShowToast(false)} />
      <LocationDebugPanel
        opened={isLocationDebugOpen}
        onClose={() => setIsLocationDebugOpen(false)} />
      {isColorLabelActive && !isDesktop && (
        <div className={styles.floatingBtnWrapper}>
          <ListBulletIcon
            fill="white"
            style={{
              cursor: 'pointer',
              color: isColorLabelOnView ? "#FF9D00" : "white"
            }}
            onClick={() => onColorLabeOnViewChange(!isColorLabelOnView)}
            width='35px'
            height='35px' />
        </div>
      )}
    </Box >
  );
});

export default MapCanvas;